import { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { 
  User, 
  Mail, 
  Building2, 
  Crown,
  LogOut,
  Loader2,
  FileText,
  Send
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const ProfilePage = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [monthlyReport, setMonthlyReport] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const fetchReportPrefs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/reports/preferences`, { headers: { Authorization: `Bearer ${token}` } });
      setMonthlyReport(res.data.monthly_report_enabled || false);
    } catch (err) {
      // Silently fail - non-critical preference load
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchReportPrefs();
  }, [token, fetchReportPrefs]);

  const toggleMonthlyReport = async (enabled) => {
    setReportLoading(true);
    try {
      await axios.put(`${API}/reports/preferences?monthly_report_enabled=${enabled}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMonthlyReport(enabled);
      toast.success(enabled ? "Monthly reports enabled" : "Monthly reports disabled");
    } catch (err) {
      toast.error("Failed to update preference");
    } finally {
      setReportLoading(false);
    }
  };

  const sendTestReport = async () => {
    setSendingReport(true);
    try {
      await axios.post(`${API}/reports/send-monthly`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Report sent to your email!");
    } catch (err) {
      toast.error("Failed to send report");
    } finally {
      setSendingReport(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      toast.success("Logged out successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to logout");
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'enterprise': return 'bg-violet-500/10 text-violet-400 border-violet-500/30';
      case 'professional': return 'bg-primary/10 text-primary border-primary/30';
      default: return 'bg-slate-700/50 text-slate-400 border-slate-600';
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
        <div>
          <h1 className="text-3xl font-bold text-white">Profile</h1>
          <p className="text-slate-400 mt-1">Manage your account settings</p>
        </div>

        {/* Profile Info */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center">
                <span className="text-3xl font-bold text-primary">
                  {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                </span>
              </div>
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {user?.first_name} {user?.last_name}
                </h3>
                <p className="text-slate-400">{user?.email}</p>
                <Badge className={`mt-2 capitalize ${getPlanBadgeColor(user?.subscription_plan)}`}>
                  <Crown className="w-3 h-3 mr-1" />
                  {user?.subscription_plan} Plan
                </Badge>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-slate-800">
              <div className="space-y-2">
                <Label className="text-slate-400">First Name</Label>
                <Input 
                  value={user?.first_name || ""} 
                  disabled 
                  className="input-default opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Last Name</Label>
                <Input 
                  value={user?.last_name || ""} 
                  disabled 
                  className="input-default opacity-50"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input 
                    value={user?.email || ""} 
                    disabled 
                    className="pl-10 input-default opacity-50"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-400">Account Type</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input 
                    value={user?.account_type || "Individual"} 
                    disabled 
                    className="pl-10 input-default opacity-50 capitalize"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-primary" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium capitalize">{user?.subscription_plan} Plan</p>
                <p className="text-slate-400 text-sm">
                  {user?.subscription_plan === 'free' 
                    ? 'Limited to 2 assets' 
                    : user?.subscription_plan === 'professional'
                    ? 'Up to 20 assets with AI features'
                    : 'Unlimited assets with full features'
                  }
                </p>
              </div>
              {user?.subscription_plan !== 'enterprise' && (
                <Link to="/pricing">
                  <Button className="btn-primary" size="sm" data-testid="upgrade-plan-btn">
                    Upgrade
                  </Button>
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Reports */}
        <Card className="card-dashboard">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Monthly Reports
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Portfolio Summary Email</p>
                <p className="text-slate-400 text-sm">Receive a monthly portfolio report on the 1st of each month</p>
              </div>
              <Switch
                checked={monthlyReport}
                onCheckedChange={toggleMonthlyReport}
                disabled={reportLoading}
                data-testid="monthly-report-toggle"
              />
            </div>
            <div className="flex items-center justify-between pt-4 border-t border-slate-800">
              <div>
                <p className="text-white font-medium">Send Test Report</p>
                <p className="text-slate-400 text-sm">Preview what the monthly email looks like</p>
              </div>
              <Button
                size="sm"
                className="btn-primary"
                onClick={sendTestReport}
                disabled={sendingReport}
                data-testid="send-test-report-btn"
              >
                {sendingReport ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {sendingReport ? "Sending..." : "Send Now"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Card className="card-dashboard border-red-500/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white font-medium">Sign Out</p>
                <p className="text-slate-400 text-sm">Log out of your account</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                disabled={loading}
                className="border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                data-testid="logout-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <LogOut className="w-4 h-4 mr-2" />
                )}
                Logout
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ProfilePage;
